<template>
  <v-container fluid class="py-0">
    <v-row>
      <v-col cols="12" class="mx-2">
        <v-tabs
          v-model="tabs"
          fixed-tabs
          slider-color="black"
          color="#616162"
          height="38"
        >
          <v-tab :disabled="!!addProcessing">
            Group Users
            <v-icon v-if="!!addProcessing" size="11"
              >fas fa-spinner fa-spin</v-icon
            >
          </v-tab>
          <v-tab :disabled="!!deleteProcessing">
            All Users
            <v-icon v-if="!!deleteProcessing" size="11"
              >fas fa-spinner fa-spin</v-icon
            >
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <Loader
      v-if="loading"
      text="Loading users"
      size="small"
      class="mt-5"
    ></Loader>

    <CommsUserList
      v-if="!loading"
      :type="type"
      :group-users="users"
      @add="handleAddToGroup"
      @remove="handleRemoveFromGroup"
    ></CommsUserList>
  </v-container>
</template>

<script>
import lodashIsEqual from "lodash/isEqual";
import lodashDebounce from "lodash/debounce";

import { MODULE_PREFIX_MAP, MODULE_PREFIX_HOME } from "@/store";

import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { GET_DETAILS_SIDEBAR } from "@/store/actions.type";

import {
  saveUserToGroup,
  deleteUserFromGroup,
  getGroupUsers,
  groupMemberIcon,
  groupMemberLocationsToFeatures,
  getGroupMembersLocation,
} from "@/services/communication/communication.service";
import MASS_COMM_CONSTANTS from "../../common/constants/massComm/massComm.js";

import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

import { mapGetters } from "vuex";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getDefaultMarkerOptions } from "@/common/maps/styles";
import { mapMarkerData, removeMapData } from "@/common/maps/features";

export default {
  name: "CommsGroupDetails",
  components: {
    CommsUserList: () => import("@/components/communication/CommsUserList"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    group: {
      type: Object,
    },
    showMembersGroup: {
      type: Object,
    },
  },
  data: function () {
    return {
      loading: false,
      tabs: 0,
      type: "users_group",
      currentGroup: this.group ? this.group : null,
      users: null,
      addProcessing: false,
      deleteProcessing: false,
      layerRequestTimestamps: {},
    };
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
  },
  mounted() {
    if (this.currentGroup && this.currentGroup.groupId) {
      this.getUsers();
    }
  },
  methods: {
    async getUsers() {
      this.loading = true;
      this.users = await getGroupUsers({ groupId: this.currentGroup.groupId });
      this.loading = false;
    },
    async addUser(user) {
      if (this.currentGroup && this.currentGroup.groupId && user && user.id) {
        let reqDate = new Date().toISOString();
        this.addProcessing = reqDate;
        const success = await saveUserToGroup({
          groupId: this.currentGroup.groupId,
          userId: user.id,
        });
        if (this.addProcessing === reqDate) {
          this.addProcessing = false;
        }
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.mass_communication,
          EVENT_ACTIONS.clicked,
          "Add User to Group"
        );
        if (!success) {
          await this.getUsers();
        }
      }
    },
    async deleteUser(user) {
      let reqDate = new Date().toISOString();
      this.deleteProcessing = reqDate;
      const success = await deleteUserFromGroup({
        groupId: this.currentGroup.groupId,
        userId: user.id,
      });
      if (this.deleteProcessing === reqDate) {
        this.deleteProcessing = false;
      }
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.mass_communication,
        EVENT_ACTIONS.clicked,
        "Remove User from Group"
      );
      if (!success) {
        await this.getUsers();
      }
    },
    async handleAddToGroup(user) {
      await this.addUser(user);
    },
    async handleRemoveFromGroup(user) {
      await this.deleteUser(user);
    },
    removeGroupMembersFromMap() {
      if (this.map) {
        this.layerRequestTimestamps[
          MASS_COMM_CONSTANTS.GROUP_MEMBER_LAYER_ID
        ] = new Date().toISOString();
        removeMapData(this.map, MASS_COMM_CONSTANTS.GROUP_MEMBER_LAYER_ID);
        removeMapData(this.map, MASS_COMM_CONSTANTS.GEOFENCED_MEMBERS_LAYER_ID);
        removeMapData(this.map, MASS_COMM_CONSTANTS.GEOFENCE_LAYER_ID);
      }
    },
    addGroupMembersToMap: lodashDebounce(async function (group) {
      let timestamp = new Date().toISOString();
      this.layerRequestTimestamps[
        MASS_COMM_CONSTANTS.GROUP_MEMBER_LAYER_ID
      ] = timestamp;

      if (
        timestamp !==
          this.layerRequestTimestamps[
            MASS_COMM_CONSTANTS.GROUP_MEMBER_LAYER_ID
          ] ||
        !group ||
        !this.map
      ) {
        return;
      }

      let groupMembersLocation = await getGroupMembersLocation(group);
      let features = groupMemberLocationsToFeatures(groupMembersLocation);

      const SOURCE = {
        type: "FeatureCollection",
        features: features,
      };

      const OPTIONS = getDefaultMarkerOptions({
        id: MASS_COMM_CONSTANTS.GROUP_MEMBER_LAYER_ID,
        iconName: groupMemberIcon,
      });
      OPTIONS["markerLayerOptions"].layout["icon-size"] = 0.5;
      OPTIONS["markerClusterLayerOptions"].layout["icon-size"] = 0.7;

      mapMarkerData({
        id: MASS_COMM_CONSTANTS.GROUP_MEMBER_LAYER_ID,
        data: SOURCE,
        mapClickEvent: this.mapClickEvent,
        clusterMapClickEvent: this.clusterMapClickEvent,
        markerOptions: OPTIONS,
        map: this.map,
        noCluster: false,
      });
    }, 500),
    async mapClickEvent(e, features) {
      this.showProfiles(features);
    },
    async clusterMapClickEvent(e, features) {
      this.showProfiles(features);
    },
    async showProfiles(features) {
      this.detailsSidebar.features = features;
      this.detailsSidebar.visible = true;
      this.detailsSidebar.loading = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.commMemberDetails;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.commMemberDetails;

      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
    },
  },
  watch: {
    tabs() {
      this.type = this.tabs === 1 ? "users_all" : "users_group";
      this.getUsers();
    },
    group: {
      handler(newGroup, oldGroup) {
        if (newGroup) {
          if (!lodashIsEqual(newGroup, oldGroup)) {
            this.type = "users_group";
            this.tabs = 0;
          }
          this.currentGroup = newGroup;
          this.getUsers();
        }
      },
      deep: true,
    },
    showMembersGroup: {
      handler() {
        console.log(
          `A group's "show members toggle" was clicked`,
          this.showMembersGroup.groupId,
          this.showMembersGroup.name,
          this.showMembersGroup.showMembersOnMap
        );
        this.removeGroupMembersFromMap();
        if (this.showMembersGroup.showMembersOnMap) {
          this.addGroupMembersToMap(this.showMembersGroup);
        }
      },
      deep: true,
    },
  },
};
</script>

<style>
.theme--light.v-tabs > .v-tabs-bar {
  background-color: transparent !important;
}
</style>

<style scoped>
.hyp-tabs-slider {
  background-color: black !important;
  border-color: black !important;
}
</style>
